import { cn } from 'noddi-ui-common';
import * as React from 'react';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../../base/input-otp';

interface NoddiOtpProps {
  maxLength?: number;
  onChange?: (value: string) => void;
  value?: string;
  onComplete?: (value: string) => void;
  autoFocus?: boolean;
  className?: string;
  /** `large`: six separate boxes that fill the width on a phone — for a page whose only task is the code. */
  size?: 'default' | 'large';
  'aria-label'?: string;
}

export const NoddiOtp = React.forwardRef<HTMLInputElement, NoddiOtpProps>(
  (
    {
      maxLength = 6,
      onChange,
      value,
      onComplete,
      autoFocus = true,
      className,
      size = 'default',
      'aria-label': ariaLabel
    },
    ref
  ) => {
    const isLarge = size === 'large';
    const handleChange = (newValue: string) => {
      onChange?.(newValue);

      // Call onComplete when the OTP is fully entered
      if (newValue.length === maxLength && onComplete) {
        onComplete(newValue);
      }
    };

    return (
      <InputOTP
        ref={ref}
        maxLength={maxLength}
        pattern='[0-9]*'
        type='tel'
        inputMode='numeric'
        onChange={handleChange}
        value={value}
        // oxlint-disable-next-line jsx-a11y/no-autofocus -- opt-in prop forwarded to the OTP field; the screens that pass it exist only to take this code
        autoFocus={autoFocus}
        aria-label={ariaLabel}
        className='gap-3'
      >
        <InputOTPGroup
          className={
            isLarge
              ? 'grid w-full grid-cols-6 gap-2 sm:flex sm:w-fit'
              : 'w-full justify-between sm:w-fit sm:justify-normal'
          }
        >
          {Array.from({ length: maxLength }, (_, index) => (
            <InputOTPSlot
              key={index}
              index={index}
              className={cn(
                isLarge
                  ? 'h-15 w-auto rounded-lg border text-2xl font-semibold text-primary first:rounded-lg last:rounded-lg data-[active]:border-primary data-[active]:ring-0 data-[filled]:border-primary/60 sm:w-16'
                  : 'flex-1 sm:size-9 sm:flex-none',
                className
              )}
            />
          ))}
        </InputOTPGroup>
      </InputOTP>
    );
  }
);

NoddiOtp.displayName = 'NoddiOtp';
